import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import { fetchGetAdmissionDraftByUser } from '@octopus/api';
import {
  admissionDraftStatuses,
  membershipTypes,
} from '@octopus/onboarding-types';

import { MembershipContext, UserContext } from '../types';

import { appContextQueryConfig } from './constants';
import { useLocalStorage } from './useLocalStorage';

const KEY = 'app-context-admissionDraft';

export const useAdmissionDraft = (
  membership: MembershipContext,
  user: UserContext,
) => {
  const { localStorageContext, setLocalStorageContext } = useLocalStorage(KEY);
  const { membershipType, organizationId } = membership || {};
  const { userId, personId } = user || {};

  const isOwnerOrInternal =
    membershipType === membershipTypes.owner ||
    membershipType === membershipTypes.internal;

  const doesNotHavePersonId = !personId;

  const isEnabled =
    !!organizationId && !!userId && isOwnerOrInternal && doesNotHavePersonId;

  const query = useQuery({
    queryKey: ['admissionDraft', organizationId, userId],
    initialData: isEnabled ? localStorageContext : null,
    queryFn: () =>
      fetchGetAdmissionDraftByUser({
        pathParams: { organizationId, userId },
      }).then((draft) => {
        const draftId = draft?.draftId;
        return draft?.draftStatus !== admissionDraftStatuses.archived
          ? { draftId }
          : null;
      }),
    enabled: isEnabled,
    ...appContextQueryConfig,
  });

  useEffect(() => {
    if (query?.data) {
      setLocalStorageContext(query?.data);
    }
  }, [query?.data]);

  // Ensure hook always returns null for data when not enabled
  if (!isEnabled) {
    return {
      ...query,
      data: null as { draftId: string } | null,
      isLoading: false,
    };
  }

  return query;
};
